import {
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  listAll,
} from 'firebase/storage';
import type { UploadTaskSnapshot } from 'firebase/storage';
import { storage } from './config';

export type UploadProgressCallback = (progress: number) => void;

export const storageService = {
  /**
   * Upload a file to Firebase Storage
   */
  uploadFile: async (
    path: string,
    file: File | Blob,
    onProgress?: UploadProgressCallback
  ): Promise<string> => {
    const storageRef = ref(storage, path);

    if (onProgress) {
      // Use resumable upload for progress tracking
      const uploadTask = uploadBytesResumable(storageRef, file);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot: UploadTaskSnapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            onProgress(progress);
          },
          reject,
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          }
        );
      });
    }

    // Simple upload without progress
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  },

  /**
   * Get download URL for a file
   */
  getFileUrl: async (path: string): Promise<string> => {
    const storageRef = ref(storage, path);
    return getDownloadURL(storageRef);
  },

  /**
   * Delete a file from storage
   */
  deleteFile: async (path: string): Promise<void> => {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
  },

  /**
   * List all files in a directory
   */
  listFiles: async (path: string): Promise<string[]> => {
    const storageRef = ref(storage, path);
    const result = await listAll(storageRef);
    return Promise.all(result.items.map((item) => getDownloadURL(item)));
  },

  /**
   * Generate a unique file path with timestamp
   */
  generateFilePath: (folder: string, fileName: string, userId: string): string => {
    const timestamp = Date.now();
    const extension = fileName.split('.').pop();
    const sanitizedName = fileName.replace(/[^a-zA-Z0-9]/g, '_');
    return `${folder}/${userId}/${timestamp}_${sanitizedName}.${extension}`;
  },
};
